import { useState, useEffect } from "react";
import { auth } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile
} from "firebase/auth";
import { useNavigate, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, Eye, EyeOff, ArrowRight, UserPlus, LogIn, ShieldAlert, Loader2, User, Home } from "lucide-react";
import { Helmet } from "react-helmet-async";

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [timer, setTimer] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false); // 🔥 Terms Checkbox State 🔥

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
  });

  const navigate = useNavigate();

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((error) => {
      console.error("Persistence setting failed", error);
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // ഗൂഗിൾ ലോഗിൻ അല്ലെങ്കിൽ വെരിഫൈ ചെയ്ത അക്കൗണ്ട് ആണെങ്കിൽ നേരെ പ്രൊഫൈലിലേക്ക് മാറ്റുന്നു
      if (user && user.emailVerified) {
        navigate("/profile");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((p) => p - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  // 🔥 കൃത്യമായ വാലിഡേഷൻ 🔥
  const validate = () => {
    if (!isLogin && !form.name.trim()) return toast.error("Please enter your full name"), false;
    if (!form.email) return toast.error("Please enter your email"), false;
    if (!/\S+@\S+\.\S+/.test(form.email)) return toast.error("Invalid email format"), false;
    if (!form.password) return toast.error("Please enter a password"), false;
    if (form.password.length < 6) return toast.error("Password must be at least 6 characters"), false;
    if (!isLogin && !acceptedTerms) return toast.error("You must accept the Terms & Privacy Policy"), false;
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsLoading(true);

    try {
      if (isLogin) {
        // --- LOGIN LOGIC ---
        const userCred = await signInWithEmailAndPassword(auth, form.email, form.password);

        if (!userCred.user.emailVerified) {
          setPendingEmail(form.email);
          toast.error("Please verify your email first! Check your Inbox/Spam.");
          await auth.signOut();
          setIsLoading(false);
          return;
        }

        toast.success("Login successful 🎉");
        navigate("/profile");

      } else {
        // --- SIGN UP LOGIC ---
        const userCred = await createUserWithEmailAndPassword(auth, form.email, form.password);
        
        // യൂസറുടെ പേര് ഫയർബേസ് പ്രൊഫൈലിലേക്ക് സേവ് ചെയ്യുന്നു
        await updateProfile(userCred.user, { displayName: form.name });

        try {
          await sendEmailVerification(userCred.user);
          toast.success("Verification link sent! Check your Email 📩");
        } catch (emailErr) {
          toast.error("Account created, but couldn't send email. Please login to resend.");
        }

        setPendingEmail(form.email);
        setTimer(30);
        await auth.signOut(); // വെരിഫൈ ചെയ്യാൻ നിർബന്ധിക്കാൻ ലോഗ് ഔട്ട് ചെയ്യുന്നു
        setIsLogin(true);
        setForm({ ...form, password: "" }); // സെക്യൂരിറ്റിക്ക് വേണ്ടി പാസ്‌വേഡ് ഫീൽഡ് ക്ലിയർ ചെയ്യുന്നു
      }

    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        toast.error("Account already exists! Please login.");
        setIsLogin(true); 
      } else if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        toast.error("Invalid email or password.");
      } else if (err.code === 'auth/network-request-failed') {
        toast.error("Network error! Please check your connection.");
      } else {
        toast.error("An error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 GOOGLE SIGN IN FUNCTION 🔥
  const handleGoogleSignIn = async () => {
    if (!isLogin && !acceptedTerms) {
      return toast.error("You must accept the Terms & Privacy Policy to continue");
    }

    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      toast.success("Logged in with Google 🎉");
      navigate("/profile");
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user') {
        console.log("Google Sign-in cancelled");
      } else {
        toast.error("Google Sign-in failed. Please try again.");
        console.error(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      const userCred = await signInWithEmailAndPassword(auth, pendingEmail, form.password);
      await sendEmailVerification(userCred.user);
      await auth.signOut();
      
      setTimer(30);
      toast.success("Verification email resent! Check your Spam folder too 📩");
    } catch (err) {
      toast.error("Please enter your password in the box above to resend the link.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!form.email) return toast.error("Enter your email first to reset password");
    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, form.email);
      toast.success("Password reset email sent 📩");
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        toast.error("No account found with this email.");
      } else {
        toast.error("An error occurred. Try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isLogin ? "Login" : "Sign Up"} | Info Payangadi</title>
      </Helmet>

      <div className="min-h-[100dvh] w-full flex items-center justify-center bg-[#F8FAFC] p-4 sm:p-6 relative overflow-hidden selection:bg-blue-200">
        
        {/* Background Decorative Elements */}
        <div className="absolute top-[-10%] left-[-10%] w-[300px] h-[300px] bg-blue-400/20 rounded-full blur-[80px] pointer-events-none"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[300px] h-[300px] bg-cyan-400/20 rounded-full blur-[80px] pointer-events-none"></div>

        {/* 🔥 BACK TO HOME BUTTON 🔥 */}
        {/* <Link 
          to="/" 
          className="absolute top-6 left-6 flex items-center gap-2 text-slate-500 hover:text-blue-600 font-bold transition-colors bg-white px-4 py-2 rounded-full shadow-sm border border-slate-100 z-20"
        >
          <Home size={18} strokeWidth={2.5} />
          <span className="hidden sm:inline">Back to Home</span>
        </Link> */}

        {/* MAIN CARD */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="bg-white p-8 sm:p-10 rounded-[2.5rem] shadow-[0_8px_40px_rgba(0,0,0,0.06)] w-full max-w-md border border-white/50 relative z-10 my-10"
        >

          {/* HEADER */}
          <div className="flex flex-col items-center mb-8">
            <motion.div 
              key={isLogin ? "login-icon" : "signup-icon"}
              initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", damping: 20 }}
              className="w-16 h-16 bg-blue-50 text-blue-600 rounded-[1.2rem] flex items-center justify-center mb-4 shadow-sm border border-blue-100/50"
            >
              {isLogin ? <LogIn size={32} strokeWidth={2} /> : <UserPlus size={32} strokeWidth={2} />}
            </motion.div>
            
            <motion.h2
              key={isLogin ? "login-title" : "signup-title"}
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight"
            >
              {isLogin ? "Welcome Back" : "Create Account"}
            </motion.h2>
            <p className="text-slate-400 text-sm font-medium mt-1 text-center">
              {isLogin ? "Enter your details to continue" : "Join Info Payangadi community"}
            </p>
          </div>

          <div className="space-y-4">
            
            {/* GOOGLE SIGN IN BUTTON */}
            <button
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-3.5 rounded-2xl transition-all shadow-sm disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-slate-200"></div>
              <span className="px-3 text-slate-400 text-xs font-bold uppercase tracking-wider">OR</span>
              <div className="flex-1 border-t border-slate-200"></div>
            </div>

            {/* 🔥 FULL NAME INPUT (Only for Sign Up) 🔥 */}
            <AnimatePresence>
              {!isLogin && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="relative group mb-4"
                >
                  <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={form.name}
                    className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all font-semibold text-slate-800 placeholder:text-slate-400 text-sm sm:text-base outline-none"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* EMAIL INPUT */}
            <div className="relative group">
              <Mail size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type="email"
                placeholder="Email Address"
                value={form.email}
                className="w-full pl-12 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all font-semibold text-slate-800 placeholder:text-slate-400 text-sm sm:text-base outline-none"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            {/* PASSWORD INPUT */}
            <div className="relative group">
              <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              <input
                type={showPass ? "text" : "password"}
                placeholder="Password"
                value={form.password}
                className="w-full pl-12 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-400 transition-all font-semibold text-slate-800 placeholder:text-slate-400 text-sm sm:text-base outline-none"
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button
                type="button"
                onClick={() => setShowPass(!showPass)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-500 transition-colors focus:outline-none"
                aria-label="Toggle Password Visibility"
              >
                {showPass ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {/* 🔥 TERMS AND CONDITIONS CHECKBOX (Only for Sign Up) 🔥 */}
            <AnimatePresence>
              {!isLogin && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="flex items-start gap-2.5 mt-2"
                >
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="terms" className="text-xs sm:text-sm text-slate-500 leading-tight">
  I agree to the <Link to="/terms" className="text-blue-600 font-bold hover:underline">Terms of Service</Link> and <Link to="/privacy" className="text-blue-600 font-bold hover:underline">Privacy Policy</Link>.
</label>
                </motion.div>
              )}
            </AnimatePresence>

            {/* FORGOT PASSWORD */}
            <AnimatePresence>
              {isLogin && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="flex justify-end"
                >
                  <button
                    type="button"
                    onClick={handleForgot}
                    className="text-sm font-bold text-blue-600 hover:text-blue-700 transition-colors disabled:opacity-50"
                    disabled={isLoading}
                  >
                    Forgot Password?
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* MAIN EMAIL/PASS BUTTON */}
            <motion.button
              whileTap={!isLoading ? { scale: 0.97 } : {}}
              onClick={handleSubmit}
              disabled={isLoading}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-black py-4 rounded-2xl shadow-lg shadow-blue-600/25 transition-all flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <><Loader2 size={18} className="animate-spin" /> Processing...</>
              ) : (
                <>{isLogin ? "Sign In with Email" : "Create Account"} <ArrowRight size={18} strokeWidth={3} /></>
              )}
            </motion.button>
          </div>

          {/* VERIFICATION MESSAGE & TIMER */}
          <AnimatePresence>
            {isLogin && pendingEmail && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className="mt-5 bg-amber-50 border border-amber-100 p-4 rounded-2xl flex flex-col gap-2"
              >
                <div className="flex items-center gap-2">
                  <ShieldAlert className="text-amber-500" size={18} />
                  <span className="text-sm font-bold text-amber-900">Email not verified!</span>
                </div>
                <p className="text-xs font-medium text-amber-700">
                  We need to verify your email to continue. Please check your inbox or spam folder.
                </p>
                <div className="flex justify-end mt-1">
                  {timer > 0 ? (
                    <span className="text-amber-700 text-xs font-black bg-amber-100 px-3 py-1.5 rounded-lg">
                      Wait {timer}s
                    </span>
                  ) : (
                    <button
                      onClick={handleResend}
                      disabled={isLoading}
                      className="text-xs font-black text-white bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 px-4 py-1.5 rounded-lg transition-colors flex items-center gap-1"
                    >
                      {isLoading && <Loader2 size={12} className="animate-spin" />} Resend Link
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* SWITCH VIEW */}
          <div className="mt-8 text-center border-t border-slate-100 pt-6">
            <p className="text-sm font-medium text-slate-500">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => {
                  setIsLogin(!isLogin);
                  setPendingEmail(""); 
                  setTimer(0);
                  setForm({ ...form, password: "" }); // Switch ചെയ്യുമ്പോൾ പാസ്‌വേഡ് ക്ലിയർ ചെയ്യുന്നു
                }}
                disabled={isLoading}
                className="text-blue-600 hover:text-blue-700 font-black ml-1.5 transition-colors focus:outline-none disabled:opacity-50"
              >
                {isLogin ? "Sign up now" : "Login here"}
              </button>
            </p>
          </div>

        </motion.div>
      </div>
    </>
  );
}